import axios from 'axios';
import translation from 'i18n/translation.json';

export type PaymentErrorContext = 'card' | 'payment' | 'deviceToken';

export interface KushkiErrorResponse {
  code?: string | number;
  message?: string;
  error?: string;
}

interface ValidationErrorItem {
  message?: string;
  user_message?: string;
}

interface ApiErrorLike {
  status_code?: string;
  message?: string;
  detail?: string;
  title?: string;
  error_code?: string;
  data?:
    | ValidationErrorItem[]
    | { message?: string; user_message?: string; error_message?: string; error_code?: string; detail?: string };
  code?: string | number;
  error?: string;
}

const KUSHKI_3DS_CODES = new Set(['E004', 'E005', 'E006']);

const GENERIC_RUNTIME_MESSAGES = new Set(['error', 'network error', 'failed to fetch', 'an error occurred']);
const AXIOS_STATUS_MESSAGE_PATTERN = /^request failed with status code \d+$/i;

const getTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
};

const isGenericRuntimeMessage = (message: string): boolean => {
  const normalizedMessage = message.toLowerCase();

  return GENERIC_RUNTIME_MESSAGES.has(normalizedMessage) || AXIOS_STATUS_MESSAGE_PATTERN.test(normalizedMessage);
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

export const getPaymentFallbackMessage = (context: PaymentErrorContext): string => {
  if (context === 'deviceToken') {
    return translation.errors.deviceTokenError;
  }

  if (context === 'payment') {
    return translation.errors.paymentProcessingFailed;
  }

  return translation.errors.cardProcessingFailed;
};

const getKushkiCodeFallback = (code: string, context: PaymentErrorContext): string => {
  if (KUSHKI_3DS_CODES.has(code)) {
    return translation.errors.card3dsFailed;
  }

  if (context === 'deviceToken') {
    return translation.errors.deviceTokenError;
  }

  return translation.errors.cardProcessingCode.replace('{code}', code);
};

export const isKushkiErrorResponse = (error: unknown): error is KushkiErrorResponse => {
  if (!isRecord(error)) {
    return false;
  }

  const hasKushkiCode = error.code !== undefined && error.code !== null && error.code !== '';
  const hasStatusCode = typeof error.status_code === 'string';

  return hasKushkiCode && !hasStatusCode && !('response' in error);
};

export const getKushkiErrorMessage = (
  response: KushkiErrorResponse | null | undefined,
  context: PaymentErrorContext = 'card',
): string => {
  const message = getTrimmedString(response?.message) ?? getTrimmedString(response?.error);

  if (message) {
    return message;
  }

  const code = response?.code !== undefined && response?.code !== null ? String(response.code) : '';

  if (code) {
    return getKushkiCodeFallback(code, context);
  }

  return getPaymentFallbackMessage(context);
};

const getValidationMessages = (items: ValidationErrorItem[]): string | undefined => {
  const messages = items
    .map(item => getTrimmedString(item.message) ?? getTrimmedString(item.user_message))
    .filter((item): item is string => Boolean(item));

  if (messages.length === 0) {
    return undefined;
  }

  return messages.join('. ');
};

const getMessageFromApiPayload = (payload: unknown): string | undefined => {
  if (!isRecord(payload)) {
    return getTrimmedString(payload);
  }

  const apiError = payload as ApiErrorLike;
  const directMessage = getTrimmedString(apiError.detail) ?? getTrimmedString(apiError.message);

  if (directMessage && !isGenericRuntimeMessage(directMessage)) {
    return directMessage;
  }

  if (Array.isArray(apiError.data)) {
    return getValidationMessages(apiError.data);
  }

  if (isRecord(apiError.data)) {
    const nestedMessage =
      getTrimmedString(apiError.data.detail) ??
      getTrimmedString(apiError.data.message) ??
      getTrimmedString(apiError.data.user_message) ??
      getTrimmedString(apiError.data.error_message);

    if (nestedMessage) {
      return nestedMessage;
    }

    const nestedCode = getTrimmedString(apiError.data.error_code);

    if (nestedCode) {
      return translation.errors.cardProcessingCode.replace('{code}', nestedCode);
    }
  }

  const errorCode = getTrimmedString(apiError.error_code);

  if (errorCode) {
    return translation.errors.cardProcessingCode.replace('{code}', errorCode);
  }

  return undefined;
};

const isTimeoutError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const message = typeof error.message === 'string' ? error.message.toLowerCase() : '';

  return error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || message.includes('timeout');
};

const isNetworkError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const message = typeof error.message === 'string' ? error.message.toLowerCase() : '';

  return error.code === 'ERR_NETWORK' || message.includes('network error') || message.includes('failed to fetch');
};

export const isPaymentNetworkError = (error: unknown): boolean => {
  return isNetworkError(error) || isTimeoutError(error);
};

const getAxiosErrorMessage = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  if (isNetworkError(error)) {
    return translation.errors.networkError;
  }

  if (isTimeoutError(error)) {
    return translation.errors.paymentTimeout;
  }

  const responseData = error.response?.data;
  const fromResponse = getMessageFromApiPayload(responseData);

  if (fromResponse) {
    return fromResponse;
  }

  const nestedData = isRecord(responseData) ? responseData.data : undefined;

  return getMessageFromApiPayload(nestedData);
};

export const getPaymentApiErrorMessage = (error: unknown, context: PaymentErrorContext = 'payment'): string => {
  const fallback = getPaymentFallbackMessage(context);

  if (!error) {
    return fallback;
  }

  if (isKushkiErrorResponse(error)) {
    return getKushkiErrorMessage(error, context);
  }

  const axiosMessage = getAxiosErrorMessage(error);

  if (axiosMessage) {
    return axiosMessage;
  }

  const fromPayload = getMessageFromApiPayload(error);

  if (fromPayload && !isGenericRuntimeMessage(fromPayload)) {
    return fromPayload;
  }

  if (error instanceof Error) {
    const runtimeMessage = getTrimmedString(error.message);

    if (runtimeMessage && !isGenericRuntimeMessage(runtimeMessage)) {
      return runtimeMessage;
    }
  }

  return fallback;
};
